/**
 * The eight engravings — public-domain prints from The Metropolitan Museum of
 * Art's Open Access collection (CC0), one per moment where the app has the
 * user's attention and nothing is at stake (D-7's surfaces).
 *
 * Stored once each as a greyscale WebP (`public/images/engravings/`, all eight, precached) and coloured per theme in CSS (`<Engraving>`), so
 * there are not sixteen baked files to keep in step with the palette. Credits
 * live here, for the About screen, and in `CREDITS.md` beside the files.
 */

export type EngravingName =
  | "anchored-ships"
  | "open-sea"
  | "zuyder-zee"
  | "sea-horse"
  | "arion"
  | "storm-water"
  | "storm-boat"
  | "east-indiaman";

export interface EngravingCredit {
  /** Where it appears. */
  slot: string;
  title: string;
  artist: string;
  date: string;
  /** The Met object page. */
  url: string;
  width: number;
  height: number;
}

export const ENGRAVINGS: Readonly<Record<EngravingName, EngravingCredit>> = {
  "anchored-ships": {
    slot: "Family",
    title: 'Four-master and Two Three-masters Anchored near a Fortified Island, from "The Sailing Vessels"',
    artist: "Pieter Bruegel the Elder",
    date: "1561–65",
    url: "https://www.metmuseum.org/art/collection/search/338712",
    width: 720,
    height: 529,
  },
  "east-indiaman": {
    slot: "Savings",
    title: "Dutch East Indiaman",
    artist: "Wenceslaus Hollar",
    date: "1647",
    url: "https://www.metmuseum.org/art/collection/search/361533",
    width: 720,
    height: 433,
  },
  "open-sea": {
    slot: "Sign-in and onboarding",
    title: "Armed Three-Master on the Open Sea Accompanied by a Galley, from The Sailing Vessels",
    artist: "Pieter Bruegel the Elder",
    date: "1561–65",
    url: "https://www.metmuseum.org/art/collection/search/383042",
    width: 720,
    height: 908,
  },
  "zuyder-zee": {
    slot: "Empty ledger",
    title: "Zuyder Zee",
    artist: "Wenceslaus Hollar",
    date: "1635",
    url: "https://www.metmuseum.org/art/collection/search/360858",
    width: 720,
    height: 447,
  },
  "sea-horse": {
    slot: "Agent, empty",
    title: "A Sea Horse and a Dolphin",
    artist: "Giovanni Domenico Tiepolo",
    date: "mid-18th–early 19th century",
    url: "https://www.metmuseum.org/art/collection/search/459768",
    width: 720,
    height: 316,
  },
  arion: {
    slot: "Tide report",
    title: "Arion on a Dolphin",
    artist: "Jan Muller",
    date: "ca. 1590",
    url: "https://www.metmuseum.org/art/collection/search/374006",
    width: 720,
    height: 709,
  },
  "storm-water": {
    slot: "Error screen",
    title: "Water, a ship at sea during a storm, from “The Four Elements”",
    artist: "Stefano della Bella",
    date: "ca. 1645–50",
    url: "https://www.metmuseum.org/art/collection/search/410876",
    width: 720,
    height: 307,
  },
  "storm-boat": {
    slot: "Offline — the agent needs a connection",
    title: "A Storm at Sea with a Large Ship and a Small Boat with Two Figures",
    artist: "Carl August Ehrensvärd",
    date: "1765–1800",
    url: "https://www.metmuseum.org/art/collection/search/651682",
    width: 720,
    height: 332,
  },
};

/** Where the file is served from. */
export function engravingSrc(name: EngravingName): string {
  return `/images/engravings/${name}.webp`;
}
